import { isPairedSumupReader, isSumupReader } from "@/lib/readerRules";
import {
  readerLiveStatusClassification,
  type ReaderLiveStatusBucket,
} from "@/lib/readerDeviceStatus";
import { ReaderStatus, type ReaderInventory } from "@/types";

export type PairingFilter = "all" | "not_paired" | typeof ReaderStatus.PAIRED;
export type DeviceStatusFilter = "all" | ReaderLiveStatusBucket;

export function matchesPairingFilter(
  reader: ReaderInventory,
  pairingFilter: PairingFilter,
): boolean {
  if (pairingFilter === "all") return true;
  if (pairingFilter === "not_paired") {
    return (
      reader.status === ReaderStatus.CREATED ||
      reader.status === ReaderStatus.NOT_PAIRED
    );
  }
  return reader.status === pairingFilter;
}

export function matchesDeviceStatusFilter(
  reader: ReaderInventory,
  deviceStatusFilter: DeviceStatusFilter,
): boolean {
  if (deviceStatusFilter === "all") return true;
  return readerLiveStatusClassification(reader) === deviceStatusFilter;
}

/** Pairing tab counts among readers matching the current device-status filter. */
export function countReadersByPairing(
  readers: ReaderInventory[],
  deviceStatusFilter: DeviceStatusFilter,
): Record<PairingFilter, number> {
  const counts: Record<PairingFilter, number> = {
    all: 0,
    not_paired: 0,
    [ReaderStatus.PAIRED]: 0,
  };
  for (const reader of readers) {
    if (!matchesDeviceStatusFilter(reader, deviceStatusFilter)) continue;
    counts.all += 1;
    if (reader.status === ReaderStatus.PAIRED) {
      counts[ReaderStatus.PAIRED] += 1;
    } else if (matchesPairingFilter(reader, "not_paired")) {
      counts.not_paired += 1;
    }
  }
  return counts;
}

/** Device-status tab counts among readers matching the current pairing filter. */
export function countReadersByDeviceStatus(
  readers: ReaderInventory[],
  pairingFilter: PairingFilter,
): Record<DeviceStatusFilter, number> {
  const counts: Record<DeviceStatusFilter, number> = {
    all: 0,
    online: 0,
    unavailable: 0,
    offline: 0,
    unknown: 0,
  };
  for (const reader of readers) {
    if (!matchesPairingFilter(reader, pairingFilter)) continue;
    counts.all += 1;
    const classified = readerLiveStatusClassification(reader);
    if (classified) {
      counts[classified] += 1;
    }
  }
  return counts;
}

export function canPair(reader: ReaderInventory): boolean {
  return (
    isSumupReader(reader) &&
    reader.status !== ReaderStatus.PAIRED &&
    !reader.assigned_device
  );
}

export function canUnpair(reader: ReaderInventory): boolean {
  return isPairedSumupReader(reader) && !reader.assigned_device;
}

export function canChangeRegion(reader: ReaderInventory): boolean {
  return !reader.assigned_device && !reader.region_locked_at;
}

export function canDelete(reader: ReaderInventory): boolean {
  return !reader.assigned_device;
}

export function canUnlinkFromDevice(reader: ReaderInventory): boolean {
  return Boolean(reader.assigned_device);
}
